// Indian number formatting utility (lakhs/crores)
export function formatINR(n) {
  if (n === null || n === undefined || isNaN(n)) return "0";
  const num = Math.round(Number(n));
  const isNeg = num < 0;
  const x = Math.abs(num).toString();
  if (x.length <= 3) return (isNeg ? "-" : "") + x;
  const lastThree = x.slice(-3);
  const other = x.slice(0, -3);
  const formatted = other.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree;
  return (isNeg ? "-" : "") + formatted;
}

// EMI calculation
export function calcEMI(principal, annualRatePct, years) {
  const P = Number(principal);
  const N = Number(years) * 12;
  const R = Number(annualRatePct) / 100 / 12;
  if (R === 0) {
    const emi = P / N;
    return {
      emi,
      totalPayment: emi * N,
      totalInterest: 0,
      principalPct: 100,
      interestPct: 0,
      months: N,
    };
  }
  const pow = Math.pow(1 + R, N);
  const emi = (P * R * pow) / (pow - 1);
  const totalPayment = emi * N;
  const totalInterest = totalPayment - P;
  const principalPct = (P / totalPayment) * 100;
  const interestPct = (totalInterest / totalPayment) * 100;
  return { emi, totalPayment, totalInterest, principalPct, interestPct, months: N };
}


// SIP (Systematic Investment Plan) calculation
// Future Value of an annuity-due (monthly contributions at start of month).
// M = P * ( ((1+i)^n - 1) / i ) * (1+i)
export function calcSIP(monthlyInvestment, expectedReturnPct, years) {
  const P = Number(monthlyInvestment);
  const n = Number(years) * 12;
  const i = Number(expectedReturnPct) / 100 / 12;
  const invested = P * n;
  let maturity;
  if (i === 0) {
    maturity = invested;
  } else {
    maturity = P * ((Math.pow(1 + i, n) - 1) / i) * (1 + i);
  }
  const gains = maturity - invested;
  const total = maturity;
  const investedPct = total > 0 ? (invested / total) * 100 : 100;
  const gainsPct = total > 0 ? (gains / total) * 100 : 0;
  return { maturity, invested, gains, investedPct, gainsPct, months: n };
}


// Fixed Deposit calculation (quarterly compounding — Indian standard)
// A = P × (1 + r/n)^(n*t)
export function calcFD(principal, annualRatePct, months, compoundingPerYear = 4) {
  const P = Number(principal);
  const r = Number(annualRatePct) / 100;
  const t = Number(months) / 12;
  const n = Number(compoundingPerYear);
  const maturity = P * Math.pow(1 + r / n, n * t);
  const interest = maturity - P;
  const total = maturity;
  const principalPct = total > 0 ? (P / total) * 100 : 100;
  const interestPct = total > 0 ? (interest / total) * 100 : 0;
  return { maturity, principal: P, interest, principalPct, interestPct, months: Number(months) };
}

// Recurring Deposit calculation (quarterly compounding — Indian standard)
// M = R × ((1+i)^n - 1) / (1 - (1+i)^(-1/3))   where i = annualRate/4, n = months/3
export function calcRD(monthlyDeposit, annualRatePct, months) {
  const R = Number(monthlyDeposit);
  const M = Number(months);
  const i = Number(annualRatePct) / 100 / 4;
  const n = M / 3;
  const invested = R * M;
  let maturity;
  if (i === 0) {
    maturity = invested;
  } else {
    const num = Math.pow(1 + i, n) - 1;
    const den = 1 - Math.pow(1 + i, -1 / 3);
    maturity = R * (num / den);
  }
  const interest = maturity - invested;
  const total = maturity;
  const investedPct = total > 0 ? (invested / total) * 100 : 100;
  const interestPct = total > 0 ? (interest / total) * 100 : 0;
  return { maturity, invested, interest, investedPct, interestPct, months: M };
}


// Lumpsum — one-time investment with annual compounding
// A = P × (1 + r)^t
export function calcLumpsum(principal, annualReturnPct, years) {
  const P = Number(principal);
  const r = Number(annualReturnPct) / 100;
  const t = Number(years);
  const maturity = P * Math.pow(1 + r, t);
  const gains = maturity - P;
  const total = maturity;
  const investedPct = total > 0 ? (P / total) * 100 : 100;
  const gainsPct = total > 0 ? (gains / total) * 100 : 0;
  return { maturity, invested: P, gains, investedPct, gainsPct };
}

// PPF — annual deposit, annual compounding (deposits assumed at start of year — annuity-due)
// FV = P × ((1+r)^n - 1)/r × (1+r)
export function calcPPF(annualDeposit, annualRatePct, years) {
  const P = Number(annualDeposit);
  const r = Number(annualRatePct) / 100;
  const n = Number(years);
  const invested = P * n;
  let maturity;
  if (r === 0) {
    maturity = invested;
  } else {
    maturity = P * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
  }
  const gains = maturity - invested;
  const total = maturity;
  const investedPct = total > 0 ? (invested / total) * 100 : 100;
  const gainsPct = total > 0 ? (gains / total) * 100 : 0;
  return { maturity, invested, gains, investedPct, gainsPct };
}

// EPF wage ceiling for the employer's EPS (pension) share — raised from
// Rs.15,000 to Rs.25,000 by S.O. 5109(E), effective 17 Sep 2026.
export const EPF_WAGE_CEILING = 25000;

// Monthly EPF credit: employee 12% of basic + employer 12% of basic minus the
// 8.33% (of basic, capped at the wage ceiling) that goes to EPS.
export function epfMonthlyContribution(basic) {
  const eps = 0.0833 * Math.min(basic, EPF_WAGE_CEILING);
  return 0.12 * basic + (0.12 * basic - eps);
}

// EPF — month-by-month simulation, the way EPFO computes it: interest at
// rate/12 on each month's running balance, credited once at year end (no
// compounding within the year). Basic salary grows once a year.
export function calcEPF(monthlyBasic, growthPct, annualRatePct, years) {
  const g = Number(growthPct) / 100;
  const r = Number(annualRatePct) / 100;
  const yrs = Math.max(0, Math.floor(Number(years)));
  let basic = Number(monthlyBasic);
  let balance = 0;
  let totalContribution = 0;
  for (let y = 0; y < yrs; y++) {
    const monthly = epfMonthlyContribution(basic);
    let yearInterest = 0;
    for (let m = 0; m < 12; m++) {
      balance += monthly;
      totalContribution += monthly;
      yearInterest += balance * (r / 12);
    }
    balance += yearInterest;
    basic = basic * (1 + g);
  }
  const maturity = balance;
  const gains = maturity - totalContribution;
  const total = maturity;
  const investedPct = total > 0 ? (totalContribution / total) * 100 : 100;
  const gainsPct = total > 0 ? (gains / total) * 100 : 0;
  return { maturity, invested: totalContribution, gains, investedPct, gainsPct };
}

// NPS exit split at normal exit (age 60 / retirement), per the PFRDA (Exits and
// Withdrawals under NPS) (Amendment) Regulations, 2025.
//   Government sector:      min 40% annuity, up to 60% lump sum.
//   Non-government (All Citizen / Corporate):
//     corpus <= Rs.8L     → up to 100% lump sum, no annuity required
//     Rs.8L – Rs.12L      → up to Rs.6L lump sum; balance via systematic unit
//                           redemption (min 6 yrs) or annuity
//     corpus > Rs.12L     → up to 80% lump sum, min 20% annuity
export const NPS_SLAB_SMALL = 800000;
export const NPS_SLAB_MID = 1200000;
export const NPS_MID_LUMPSUM_CAP = 600000;

export function npsExitSplit(corpus, sector = "nongov") {
  if (sector === "gov") {
    return { lumpsum: corpus * 0.6, balance: corpus * 0.4, balanceKind: "annuity" };
  }
  if (corpus <= NPS_SLAB_SMALL) {
    return { lumpsum: corpus, balance: 0, balanceKind: "none" };
  }
  if (corpus <= NPS_SLAB_MID) {
    const lumpsum = NPS_MID_LUMPSUM_CAP;
    return { lumpsum, balance: corpus - lumpsum, balanceKind: "sur" };
  }
  return { lumpsum: corpus * 0.8, balance: corpus * 0.2, balanceKind: "annuity" };
}

// NPS — monthly contribution till retirement, then the exit split above.
// FV uses annuity-due (start-of-month) like SIP. Monthly pension assumes the
// balance (not taken as lump sum) is used to buy an annuity.
export function calcNPS(monthly, annualReturnPct, currentAge, retirementAge, annuityRatePct = 6, sector = "nongov") {
  const P = Number(monthly);
  const years = Math.max(0, Number(retirementAge) - Number(currentAge));
  const i = Number(annualReturnPct) / 100 / 12;
  const n = years * 12;
  const invested = P * n;
  let corpus;
  if (i === 0) {
    corpus = invested;
  } else {
    corpus = P * ((Math.pow(1 + i, n) - 1) / i) * (1 + i);
  }
  const { lumpsum, balance: annuityCorpus, balanceKind } = npsExitSplit(corpus, sector);
  const monthlyPension = (annuityCorpus * (Number(annuityRatePct) / 100)) / 12;
  const gains = corpus - invested;
  const investedPct = corpus > 0 ? (invested / corpus) * 100 : 100;
  const gainsPct = corpus > 0 ? (gains / corpus) * 100 : 0;
  return {
    corpus,
    lumpsum,
    annuityCorpus,
    balanceKind,
    monthlyPension,
    invested,
    gains,
    investedPct,
    gainsPct,
    years,
  };
}


// Bond returns (coupons reinvested at the same rate; effective compounding at chosen frequency).
// Also returns the per-period coupon and the simple coupon total (not reinvested).
// freqPerYear: 1=annual, 2=semi, 4=quarterly, 12=monthly
export function calcBond(principal, couponRatePct, years, freqPerYear = 2) {
  const P = Number(principal);
  const r = Number(couponRatePct) / 100;
  const t = Number(years);
  const n = Number(freqPerYear);
  const maturity = P * Math.pow(1 + r / n, n * t);
  const interest = maturity - P;
  const periodicCoupon = (P * r) / n;
  const simpleCouponTotal = P * r * t;
  const total = maturity;
  const principalPct = total > 0 ? (P / total) * 100 : 100;
  const interestPct = total > 0 ? (interest / total) * 100 : 0;
  return {
    maturity,
    principal: P,
    interest,
    periodicCoupon,
    simpleCouponTotal,
    principalPct,
    interestPct,
    freqPerYear: n,
  };
}
